import { useSyncExternalStore } from 'react';
import { accountApi, AccountApiError, currentAccount } from './accountSession';
import { activateUserCache, scopedStorage, onStorageWrite, writeCloudCache } from './standaloneStorage';
import { emptyCloudItem, isUserDataKey, type CloudSnapshot, type SyncRequest } from './userDataKeys';
import { mergeLegacyValue, mergeUserValue } from './userDataMerge';
import { cacheLocalValue, loadLocalCache, loadJournal, saveJournal, type SyncJournal } from './userDataJournal';

interface SyncStatus {
  accountId: string; phase: 'loading' | 'ready' | 'error'; syncing: boolean;
  pending: number; error: string; updatedAt: number; remote: boolean; epoch: number;
}
let status: SyncStatus = { accountId: '', phase: 'loading', syncing: false, pending: 0, error: '', updatedAt: 0, remote: false, epoch: 0 };
const listeners = new Set<() => void>();
let publishQueued = false;
function publish(update: Partial<SyncStatus>) {
  status = { ...status, ...update };
  // Cache initialization can publish during React render; notify subscribers after it completes.
  if (publishQueued) return;
  publishQueued = true;
  queueMicrotask(() => {
    publishQueued = false;
    listeners.forEach(listener => listener());
  });
}
export function useUserDataSync() {
  return useSyncExternalStore(listener => { listeners.add(listener); return () => { listeners.delete(listener); }; }, () => status);
}
let current: UserDataSync | undefined;
const bytes = (value: string) => new TextEncoder().encode(value).length;
const message = (error: unknown) => error instanceof TypeError || error instanceof SyntaxError
  ? '网络连接中断，记录已保留在本机，联网后会继续同步'
  : error instanceof Error ? error.message : '同步失败，记录已保留在本机';

class UserDataSync {
  private journal: SyncJournal = { initialized: false, revision: 0, items: {} };
  private timer = 0;
  private polling = 0;
  private stopped = false;
  private running = false;
  private retryDelay = 3000;
  private remote?: CloudSnapshot;
  private controller = new AbortController();
  private offWrite: () => void;
  private prefix: string;
  private offCache?: () => void;
  private writes = new Set<Promise<void>>();
  private failedWrites = new Map<string, string | null>();
  private external = false;
  private oversized = '';
  constructor(readonly userId: string) {
    this.prefix = `u_${userId}:`;
    this.offWrite = onStorageWrite((key, source) => {
      if (key.startsWith(this.prefix) && isUserDataKey(key.slice(this.prefix.length)) && this.valid() && status.phase === 'ready') {
        if (source === 'external') this.external = true;
        this.pending(); this.schedule(650);
      }
    });
    window.addEventListener('online', this.retry);
    document.addEventListener('visibilitychange', this.visible);
    window.addEventListener('pagehide', this.retry);
  }
  private valid() { return !this.stopped && current === this && currentAccount()?.id === this.userId; }
  private read(key: string) { return scopedStorage.getItem(this.prefix + key); }
  private localKeys() {
    const result: string[] = [];
    for (let i = 0; i < scopedStorage.length; i++) {
      const key = scopedStorage.key(i);
      if (key?.startsWith(this.prefix) && isUserDataKey(key.slice(this.prefix.length))) result.push(key.slice(this.prefix.length));
    }
    return result;
  }
  private changes() {
    const result = [];
    for (const key of new Set([...Object.keys(this.journal.items), ...this.localKeys()])) {
      if (!isUserDataKey(key)) continue;
      const base = this.journal.items[key] || emptyCloudItem(), value = this.read(key);
      if (value !== base.value) result.push({ key, value, baseRevision: base.revision });
    }
    return result;
  }
  private pending() { if (this.valid()) publish({ pending: this.changes().length }); }
  private api(path: string, data?: unknown): Promise<CloudSnapshot> {
    return accountApi(path, data, { accountId: this.userId, signal: this.controller.signal });
  }
  private async persist() {
    while (this.writes.size) await Promise.all(this.writes);
    for (const [key, value] of this.failedWrites) {
      await cacheLocalValue(this.userId, key, value);
      if (this.failedWrites.get(key) === value) this.failedWrites.delete(key);
    }
    if (this.valid()) await saveJournal(this.userId, this.journal);
  }
  private persistLocal = (key: string, value: string | null) => {
    if (!this.valid()) return;
    const task = cacheLocalValue(this.userId, key, value).then(() => {
      if (this.read(key) === value) this.failedWrites.delete(key);
    }).catch(error => {
      this.failedWrites.set(key, this.read(key));
      if (this.valid()) publish({ error: message(error) });
    }).finally(() => { this.writes.delete(task); });
    this.writes.add(task);
  };
  async initialize() {
    try {
      this.journal = await loadJournal(this.userId);
      if (!this.valid()) return;
      const local = await loadLocalCache(this.userId);
      if (!this.valid()) return;
      const values = Object.fromEntries(this.localKeys().map(key => [key, this.read(key)]));
      this.offCache = activateUserCache(this.prefix, { ...values, ...local }, this.persistLocal,
        (key, incoming, cached) => mergeUserValue(key, this.journal.items[key]?.value ?? null, incoming, cached));
      // A lost HTTP response is replayed exactly; the server's idempotency key prevents duplicate writes.
      if (this.journal.inFlight) {
        try { await this.send(this.journal.inFlight); }
        catch (error) { if (!(error instanceof AccountApiError && error.status === 409)) throw error; }
      }
      const cloud = await this.api('/user-data');
      if (!this.valid()) return;
      await this.apply(cloud, !this.journal.initialized);
      if (!this.valid()) return;
      publish({ phase: 'ready', error: '', updatedAt: Date.now() });
      this.pending(); this.schedule(100);
      this.polling = window.setInterval(() => { if (document.visibilityState === 'visible') this.schedule(0); }, 20000);
    } catch (error) {
      if (this.valid()) publish({ phase: 'error', syncing: false, error: message(error) });
    }
  }
  private async apply(cloud: CloudSnapshot, legacy = false) {
    if (!this.valid()) return;
    const keys = new Set([...this.localKeys(), ...Object.keys(this.journal.items), ...Object.keys(cloud.items)]);
    for (const key of keys) {
      if (!isUserDataKey(key)) continue;
      const before = this.journal.items[key] || emptyCloudItem();
      const remote = cloud.items[key] || emptyCloudItem();
      if (legacy && remote.revision > 0 && this.read(key) !== remote.value && this.read(key) !== null) {
        this.journal.legacy ??= {};
        this.journal.legacy[key] = this.read(key);
      }
      const value = legacy
        ? (remote.revision > 0 ? remote.value : mergeLegacyValue(key, this.read(key), remote.value))
        : mergeUserValue(key, before.value, this.read(key), remote.value);
      writeCloudCache(this.prefix + key, value);
    }
    this.journal = { initialized: true, revision: cloud.revision, items: cloud.items, legacy: this.journal.legacy };
    await this.persist();
  }
  private async send(request: SyncRequest) {
    try {
      const response = await this.api('/user-data/sync', request);
      if (!this.valid()) return;
      for (const sent of request.changes) {
        const item = response.items[sent.key];
        if (!item) throw new Error('服务器同步响应不完整，请重试');
        // Never replace a newer local edit made while this request was in flight.
        if (this.read(sent.key) === sent.value) writeCloudCache(this.prefix + sent.key, item.value);
        this.journal.items[sent.key] = item;
      }
      this.journal.revision = Math.max(this.journal.revision, response.revision);
      delete this.journal.inFlight;
      await this.persist();
      if (this.valid()) { this.retryDelay = 3000; publish({ updatedAt: Date.now(), error: '' }); }
    } catch (error) {
      if (this.valid() && error instanceof AccountApiError && error.status === 409) {
        delete this.journal.inFlight;
        await this.persist();
      }
      if (this.valid() && error instanceof AccountApiError && [400, 413, 422].includes(error.status)) {
        delete this.journal.inFlight;
        await this.persist();
      }
      throw error;
    }
  }
  private request(): SyncRequest | null {
    this.oversized = '';
    const changes = this.changes();
    if (!changes.length) return null;
    const selected = [], limit = 10 * 1024 * 1024;
    let size = 1024;
    for (const change of changes) {
      const length = bytes(JSON.stringify(change));
      if ((change.value !== null && bytes(change.value) > 8 * 1024 * 1024) || length > limit - 1024) {
        this.oversized = '单项数据过大，暂未上传，请下载备份并减少照片后重试';
        continue;
      }
      if (selected.length && (selected.length >= 64 || size + length > limit)) break;
      size += length; selected.push(change);
    }
    if (!selected.length) return null;
    const requestId = typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
      : Array.from(crypto.getRandomValues(new Uint8Array(24)), v => v.toString(16).padStart(2, '0')).join('');
    return { requestId, changes: selected };
  }
  private stage(cloud: CloudSnapshot) {
    this.remote = cloud;
    publish({ remote: true });
  }
  async applyRemote() {
    if (!this.valid() || this.running || !this.remote) return;
    this.running = true;
    try {
      const cloud = this.remote;
      await this.apply(cloud);
      if (!this.valid()) return;
      this.remote = undefined;
      this.external = false;
      publish({ remote: false, error: '', epoch: status.epoch + 1 });
      this.pending(); this.schedule(100);
    } catch (error) { if (this.valid()) publish({ error: message(error) }); }
    finally { this.running = false; }
  }
  private async run() {
    if (!this.valid() || this.running || status.phase !== 'ready' || this.remote) return;
    this.running = true; publish({ syncing: true });
    try {
      for (let batch = 0; batch < 12 && this.valid(); batch++) {
        const request = this.journal.inFlight || this.request();
        if (!request) break;
        this.journal.inFlight = request;
        await this.persist();
        if (!this.valid()) return;
        await this.send(request);
      }
      const cloud = await this.api('/user-data');
      if (!this.valid()) return;
      if (this.external || Object.keys(cloud.items).some(key => cloud.items[key].revision !== this.journal.items[key]?.revision)
        || Object.keys(this.journal.items).some(key => !cloud.items[key])) this.stage(cloud);
      else publish({ error: this.oversized });
      this.pending();
      if (status.pending && !this.remote && !this.oversized) this.schedule(650);
    } catch (error) {
      if (!this.valid()) return;
      if (error instanceof AccountApiError && error.status === 409) {
        try { const cloud = await this.api('/user-data'); if (this.valid()) this.stage(cloud); }
        catch (fetchError) { if (this.valid()) publish({ error: message(fetchError) }); }
      } else {
        publish({ error: message(error) });
        this.schedule(this.retryDelay);
        this.retryDelay = Math.min(60000, this.retryDelay * 2);
      }
      this.pending();
    } finally { this.running = false; if (this.valid()) publish({ syncing: false }); }
  }
  private schedule(delay: number) {
    if (!this.valid()) return;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => { void this.run(); }, delay);
  }
  retry = () => { this.schedule(0); };
  acknowledgeLegacy() { delete this.journal.legacy; void this.persist().catch(() => {}); }
  async flush() {
    window.clearTimeout(this.timer);
    while (this.running && this.valid()) await new Promise(resolve => window.setTimeout(resolve, 100));
    if (this.remote) await this.applyRemote();
    await this.run();
    if (this.valid() && (this.changes().length || this.journal.inFlight)) {
      throw new Error(status.error || '记录尚未上传，请联网同步后退出');
    }
  }
  private visible = () => { if (document.visibilityState === 'visible') this.retry(); };
  stop() {
    this.stopped = true; this.controller.abort(); this.offWrite();
    this.offCache?.();
    window.clearTimeout(this.timer); window.clearInterval(this.polling);
    window.removeEventListener('online', this.retry);
    window.removeEventListener('pagehide', this.retry);
    document.removeEventListener('visibilitychange', this.visible);
  }
}

export function startUserDataSync(userId: string) {
  current?.stop();
  publish({ accountId: userId, phase: 'loading', syncing: false, pending: 0, error: '', updatedAt: 0, remote: false, epoch: 0 });
  current = new UserDataSync(userId);
  void current.initialize();
  return () => { if (current?.userId === userId) { current.stop(); current = undefined; } };
}
export function retryUserDataSync() {
  if (!current) return;
  if (status.phase === 'error') startUserDataSync(current.userId);
  else current.retry();
}
export function applyRemoteUserData() { void current?.applyRemote(); }
export async function flushUserDataSync() { await current?.flush(); }
export function acknowledgeLegacyUserData() { current?.acknowledgeLegacy(); }
